import { handleActions } from 'redux-actions';
import { getFormValues } from 'redux-form';
import cloneDeep from 'lodash/cloneDeep';
import values from 'lodash/values';
import _get from 'lodash/get';
import _omit from 'lodash/omit';
import _find from 'lodash/find';
import _xor from 'lodash/xor';

import moment from 'moment';
import qs from 'qs';

import http from '../../shared/http';
import endpoints from '../../../config/endpoints';
import { createSelectOptionsFromSysEnums } from '../../shared/form-elements/select';

import intl from '../../shared/intl';
import checkPermissions from '../../shared/check-permissions';

import { default as createCalendar } from '../../shared/calendar';
import createFilters from '../../shared/filters';
import csvExport from '../../shared/csvExport';
import {LOCATION_CHANGE} from 'react-router-redux';

const calendarOfCreationDate = createCalendar({
	namespace: 'guarantApproveRequestsList',
	stateNodeName: 'calendarOfCreationDate',
	defaultValue: {
		start: moment().subtract(7, 'days').format('YYYY-MM-DD'),
		end: moment().format('YYYY-MM-DD')
	}
});

// actions types
const FETCH_DATA_SUCCESS = 'guarant-approve-requests/fetch-data-success';
const FETCH_DATA_ERROR = 'guarant-approve-requests/fetch-data-error';

const APPEND_DATA_SUCCESS = 'guarant-approve-requests/APPEND_DATA_SUCCESS';
const LOADED_ALL = 'guarant-approve-requests/LOADED_ALL';

const TOGGLE_PROGRESS_BAR = 'guarant-approve-requests/toggle-progress-bar';
const CLEAR_PAGINATION = 'guarant-approve-requests/CLEAR_PAGINATION';
const SET_MONTHLY_PAYMENTS = 'guarant-approve-requests/set-monthly-payments';

const CLEAR = 'guarant-approve-requests/clear';
const DEFAULT_ACTION = 'guarant-approve-requests/DEFAULT_ACTION';

const TOGGLE_EXPANDED_ITEM = 'guarant-approve-requests/TOGGLE_EXPANDED_ITEM';
const REMOVE_EXPANDED_ITEM = 'guarant-approve-requests/REMOVE_EXPANDED_ITEM';
const TOGGLE_EXPANDED_HISTORY_ITEM = 'guarant-approve-requests/TOGGLE_EXPANDED_HISTORY_ITEM';
const REMOVE_EXPANDED_HISTORY_ITEM = 'guarant-approve-requests/REMOVE_EXPANDED_HISTORY_ITEM';

const FETCH_HISTORY_DATA_SUCCESS = 'guarant-approve-requests/FETCH_HISTORY_DATA_SUCCESS';
const CLEAR_HISTORY_DATA_SUCCESS = 'guarant-approve-requests/CLEAR_HISTORY_DATA_SUCCESS';

const TOGGLE_EXPANDED_DATA = 'guarant-approve-requests/TOGGLE_EXPANDED_DATA';

const TOGGLE_PROLONG_POPUP = 'guarant-approve-requests/TOGGLE_PROLONG_POPUP';
const TOGGLE_CHANGE_DATE_POPUP = 'guarant-approve-requests/TOGGLE_CHANGE_DATE_POPUP';
const CHANGE_SORT = 'guarant-approve-requests/CHANGE_SORT';

const PAGINATE_BY_TABLE = 'guarant-approve-requests/PAGINATE_BY_TABLE';

// permissions
export const allowedPermissions = {
	view: 'guarant_register_view',
	guarant_register_confirm_prolong: 'guarant_register_confirm_prolong'
};

const filters = createFilters({
	namespace: 'guarantApproveRequestsList',
	stateNodeName: 'filters',
	queryBuildFunc: () => '',
	listOfFilters: [
		{
			name: 'offers',
			title: 'Offers',
			placeholder: 'Add offers',
			value: [],
			multi: true,
			shouldInverse: false,
			shouldShowInverseControl: false,
			options: {
				type: 'dynamic',
				source: endpoints.LOOKUPS.OFFERS
			}
		},
		{
			name: 'affiliates',
			title: 'Affiliates',
			placeholder: 'Add affiliates',
			value: [],
			multi: true,
			shouldInverse: false,
			shouldShowInverseControl: false,
			options: {
				type: 'dynamic',
				source: endpoints.LOOKUPS.AFFILIATES
			}
		},
		{
			name: 'country_ids',
			title: 'Countries',
			placeholder: 'Add country',
			value: [],
			multi: true,
			shouldInverse: false,
			shouldShowInverseControl: false,
			options: {
				type: 'dynamic',
				source: endpoints.LOOKUPS.LOCATIONS_WITH_TRAFFIC
			}
		},
		{
			name: 'admins',
			title: 'Admins',
			placeholder: 'Admins',
			value: [],
			multi: true,
			shouldInverse: false,
			shouldShowInverseControl: false,
			options: {
				type: 'dynamic',
				source: endpoints.LOOKUPS.ADMINS
			}
		},
		{
			name: 'is_active_status',
			title: 'Is active status',
			placeholder: 'Is active status',
			value: [],
			multi: true,
			shouldInverse: false,
			shouldShowInverseControl: false,
			options: {
				type: 'provided',
				source: ['true', 'false'].map(reg => ({
					value: reg,
					label: reg
				}))
			}
		},
		{
			name: 'traffic_source',
			title: 'Traffic source',
			placeholder: 'Select traffic source',
			value: [],
			multi: true,
			shouldInverse: false,
			shouldShowInverseControl: false,
			options: {
				type: 'static',
				source: 'root.lists.traffic-sources'
			}
		}
	]
});

// initial state
const initialState = {
	data: null,
	calendarOfCreationDate: calendarOfCreationDate.initialState,
	isProlongPopupVisible: false,
	isChangeDatePopupVisible: false,
	prolongPopupRequestId: null,
	expandedIds: [],
	expandedItems: {},
	expandedHistoryIds: [],
	expandedHistoryItems: {},
	table: {
		columns: {
			manager: {
				index: ['manager', 'affiliate', 'country', 'offer'],
				title: ['Manager', 'Affiliate', 'Geo', 'Offer'],
				className: 'fixed-col width-200 sort-header'
			},
			id: {
				index: 'id',
				title: 'ID',
				className: 'width-100 sort-header',
				isSortable: true
			},
			start: {
				index: ['start', 'end'],
				title: ['Start', 'End'],
				className: 'width-100 sort-header',
				isSortable: true
			},
			created_at: {
				index: 'created_at',
				title: 'Created',
				className: 'width-100 sort-header',
				isSortable: true
			},
			start_leads_count: {
				index: 'start_leads_count',
				title: 'Start leads count',
				className: 'width-100 sort-header',
				isSortable: true
			},
			approve_percent: {
				index: 'approve_percent',
				title: 'Target percent',
				className: 'width-100'
			},
			payout_amount: {
				index: 'payout_amount',
				title: 'payout amount',
				className: 'width-100 sort-header',
				isSortable: true
			},
			sid: {
				index: 'sid',
				title: 'Sid',
				className: 'width-50 max-width-100'
			},
			duration: {
				index: 'duration',
				title: 'duration',
				className: 'width-100 sort-header'
			},
			is_in_progress: {
				index: 'is_in_progress',
				title: 'Активен?',
				className: 'width-100'
			},
			non_trash_leads_count: {
				index: 'leads_count',
				title: 'Обьем лидов за период',
				className: 'width-100'
			},
			target_percent: {
				index: 'target_percent',
				title: 'Approve %%',
				className: 'width-100 sort-header',
				isSortable: true
			},
			traf_resent: {
				index: 'traf_resent',
				title: 'Траффик 6 недель',
				className: 'width-50 max-width-100'
			},
			prolong_reason: {
				index: 'prolong_reason',
				title: 'Причина проделния',
				className: 'width-100'
			},
			prolonged_by_admin: {
				index: 'prolonged_by_admin',
				title: 'Продлено админом',
				className: 'width-100'
			},
			garant_expiration_comment: {
				index: 'prolonged_by_admin',
				title: 'Коментарий',
				className: 'width-100'
			},
			clickup_url: {
				index: 'clickup_url',
				title: 'Clickup',
				className: 'width-100'
			},
			actions: {
				index: 'actions',
				title: 'Actions',
				className: 'fixed-col-right width-200 approve_request_cell'
			}
		},
		pagination: {
			isLoading: false,
			isLoadedAll: false,
			limit: 1000,
			offset: 0
		}
	},
	sortColumn: {
		col: null,
		direction: null
	},
	filters: filters.initialState,
	isRequestForApprovePopupVisible: false,
	showProgressBar: false,
	error: false
};

// actions
export const actions = Object.assign(
	{
		fetchData: () => (dispatch, getState) => {
			dispatch({ type: CLEAR_PAGINATION });
			dispatch(actions.toggleProgressBar());
			if (document.isLoading) {
				return;
			}

			document.isLoading = true;
			return request(dispatch, getState)
				.then(action => {
					document.isLoading = false;
					return action;
				});
		},
		paginateData: () => (dispatch, getState) => {
			dispatch({ type: PAGINATE_BY_TABLE });
			return request(dispatch, getState, true);
		},
		toggleExpanded: (id) => (dispatch, getState) => {
			dispatch({
				type: TOGGLE_EXPANDED_ITEM,
				payload: id
			});
			dispatch({ type: REMOVE_EXPANDED_HISTORY_ITEM, payload: id });
			dispatch({ type: TOGGLE_EXPANDED_DATA, payload: id });

		},
		toggleExpandedHistory: (id) => (dispatch, getState) => {

			dispatch({
				type: TOGGLE_EXPANDED_HISTORY_ITEM,
				payload: id
			});
			dispatch({ type: REMOVE_EXPANDED_ITEM, payload: id });

			return http({
				url: `${endpoints.MANUAL_GUARANT_REQUEST.HISTORY}`.replace('{id}', id),
				method: 'get',
				addToResponse: { id },
				actions: [ FETCH_HISTORY_DATA_SUCCESS, FETCH_DATA_ERROR]
			})(dispatch);
		},
		remove: id => (dispatch, getState) => {
			const request = http({
				url: `${endpoints.MANUAL_GUARANT_REQUEST.BASE}/${id}`,
				method: 'delete',
				actions: [ DEFAULT_ACTION ]
			});

			return request(dispatch)
				.then(action => {
					if (action.type === DEFAULT_ACTION) {
						dispatch(actions.fetchData());
					}
				});
		},
		exportCsv: () => (dispatch, getState) => {
			const rows = selectors.getData(getState()) || [];
			const headers = Object.keys(rows[0]);

			const columns = rows.map(entity => headers.map(h => entity[h] || '-'));
			const rowsForExport = [].concat([ headers ], columns);

			return csvExport('garant-register', rowsForExport);
		},
		changeSort: (col) => (dispatch, getState) => {
			if (!col || !col.isSortable) {
				return;
			}

			const currentSort = selectors.getSort(getState());

			dispatch({ type: CLEAR });

			dispatch({ type: CHANGE_SORT, payload: {
				col: col.index,
				direction: !currentSort.direction || currentSort.direction === 'desc' ? 'asc' : 'desc'
			} });
			dispatch(actions.toggleProgressBar());

			return request(dispatch, getState);
		},
		prolong: (id, data) => (dispatch, getState) => {
			const request = http({
				url: `${endpoints.MANUAL_GUARANT_REQUEST.BASE}/${id}/prolong`,
				method: 'put',
				data: {
					...data,
					prolong_type: _get(data, 'prolong_type.0.value'),
					prolong_reason: _get(data, 'prolong_reason.0.value')
				},
				actions: [ DEFAULT_ACTION ]
			});

			return request(dispatch)
				.then(action => {
					if (action.type === DEFAULT_ACTION) {
						dispatch(actions.toggleProlongPopup());
						dispatch(actions.fetchData());
					}
				});
		},
		changeDate: (id, sid) => (dispatch, getState) => {
			const formValues = getFormValues('garantRegisterChangeEndDate')(getState());
			const request = http({
				url: `${endpoints.MANUAL_GUARANT_REQUEST.BASE}/${id}/period`,
				method: 'put',
				data: {
					...formValues,
					ends_at: formValues.ends_at.endOf('day').format('YYYY-MM-DD HH:mm:ss.SSSZ'),
					sid,
					garant_register_id: id
				},
				actions: [ DEFAULT_ACTION ]
			});

			return request(dispatch)
				.then(action => {
					if (action.type === DEFAULT_ACTION) {
						dispatch(actions.toggleChangeDatePopup());
						dispatch(actions.fetchData());
					}
				});
		},
		rejectProlong: (id) => (dispatch, getState) => {
			const request = http({
				url: `${endpoints.MANUAL_GUARANT_REQUEST.BASE}/${id}/prolong/reject`,
				method: 'put',
				actions: [ DEFAULT_ACTION ]
			});

			return request(dispatch)
				.then(action => {
					if (action.type === DEFAULT_ACTION) {
						dispatch(actions.fetchData());
					}
				});
		},
		approveProlong: (id, data) => (dispatch, getState) => {
			const request = http({
				url: `${endpoints.MANUAL_GUARANT_REQUEST.BASE}/${id}/prolong/approve`,
				method: 'put',
				actions: [ DEFAULT_ACTION ]
			});

			return request(dispatch)
				.then(action => {
					if (action.type === DEFAULT_ACTION) {
						dispatch(actions.fetchData());
					}
				});
		},

		clear: () => (dispatch, getState) => {
			dispatch(actions.toggleProgressBar());
			dispatch({ type: CLEAR });
			return request(dispatch, getState);
		},
		toggleProlongPopup: (prolongPopupRequestId = null) => (dispatch, getState) => {
			dispatch({ type: TOGGLE_PROLONG_POPUP, payload: prolongPopupRequestId });
		},
		toggleChangeDatePopup: (requestId = null, sid) => (dispatch, getState) => {
			dispatch({ type: TOGGLE_CHANGE_DATE_POPUP, payload: {
				changeDatePopupRequestId: requestId, changeDatePopupRequestSid: sid
			} });
		},

		toggleProgressBar: () => ({
			type: TOGGLE_PROGRESS_BAR
		})
	},
	calendarOfCreationDate.actions,
	filters.actions
);

// reducers
const reducers = Object.assign(
	{
		[LOCATION_CHANGE]: (state, action) => ({
			...initialState
		}),
		[FETCH_DATA_SUCCESS]: (state, action) => ({
			...state,
			data: action.payload.data.rows,
			showProgressBar: false,
			error: false
		}),
		[APPEND_DATA_SUCCESS]: (state, action) => ({
			...state,
			data: state.data && [...state.data, ...action.payload.data.rows],
			table: {...state.table, pagination: {...state.table.pagination, isLoading: false}}
		}),
		[LOADED_ALL]: (state, action) => ({
			...state,
			table: {...state.table, pagination: {...state.table.pagination, isLoadedAll: true}}
		}),
		[CLEAR]: (state, action) => ({  ...initialState }),
		[CLEAR_PAGINATION]: (state, action) => ({...state, table: {...state.table, pagination: {
			...initialState.table.pagination
		}}}),
		[TOGGLE_PROLONG_POPUP]: (state, action) => ({
			...state,
			isProlongPopupVisible: !state.isProlongPopupVisible,
			prolongPopupRequestId: action.payload || null
		}),
		[TOGGLE_CHANGE_DATE_POPUP]: (state, action) => ({
			...state,
			isChangeDatePopupVisible: !state.isChangeDatePopupVisible,
			...action.payload
		}),
		[SET_MONTHLY_PAYMENTS]: (state, action) => ({ ...state, monthlyPayments: action.payload || [] }),
		[TOGGLE_EXPANDED_ITEM]: (state, action) => ({
			...state, expandedIds: [..._xor(state.expandedIds, [ action.payload ])]
		}),
		[REMOVE_EXPANDED_ITEM]: (state, action) => ({
			...state, expandedIds: [...state.expandedIds.filter(item => item !== action.payload)]
		}),
		[REMOVE_EXPANDED_HISTORY_ITEM]: (state, action) => ({
			...state, expandedHistoryIds: [...state.expandedHistoryIds.filter(item => item !== action.payload)]
		}),
		[TOGGLE_EXPANDED_HISTORY_ITEM]: (state, action) => ({
			...state, expandedHistoryIds: [..._xor(state.expandedHistoryIds, [ action.payload ])]
		}),
		[FETCH_DATA_ERROR]: (state, action) => ({ ...state, error: true, showProgressBar: false }),
		[PAGINATE_BY_TABLE]: (state, action) => ({...state, table: {...state.table, pagination: {
			...state.table.pagination, offset: state.table.pagination.offset + state.table.pagination.limit,
			isLoading: true
		}}}),
		[FETCH_HISTORY_DATA_SUCCESS]: (state, action) => ({
			...state,
			expandedHistoryItems: {...state.expandedHistoryItems, [`${action.meta.id}`]: action.payload.data }
		}),
		[CLEAR_HISTORY_DATA_SUCCESS]: (state, action) => ({
			...state,
			expandedHistoryItems: {..._omit(state.expandedHistoryItems, [`${action.payload}`])}
		}),
		[TOGGLE_PROGRESS_BAR]: state => ({ ...state, showProgressBar: !state.showProgressBar }),
		[TOGGLE_PROGRESS_BAR]: state => ({ ...state, showProgressBar: !state.showProgressBar }),
		[CHANGE_SORT]: (state, action) => ({ ...state, sortColumn: action.payload })
	},
	calendarOfCreationDate.reducers,
	filters.reducers
);

// reducers
export default handleActions(reducers, cloneDeep(initialState));

// selectors
export const selectors = {
	getData: state => state.guarantApproveRequestsList.data,
	getSort: state => state.guarantApproveRequestsList.sortColumn,
	getMonthlyPayments: state => state.guarantApproveRequestsList.monthlyPayments,
	getExpandedRows: state => state.guarantApproveRequestsList.expandedRows,
	getCalendarOfCreationDate: state =>
		calendarOfCreationDate.selectors.getCalendar(
			state => state.guarantApproveRequestsList.calendarOfCreationDate, state => state
		)(state),
	getTable: state => {
		const { table, data, expandedIds } = state.guarantApproveRequestsList;
		let items = state.guarantApproveRequestsList.data ? data : [];
		let rows = [];
		for (const item of items) {
			if (!_find(rows, { id: item.id })) {
				rows.push({...item, idsCount: items.filter(i => i.id === item.id).length }); continue;
			}
			if (expandedIds.includes(item.id)) {
				rows.push({...item, isNested: true });
			}
		}
		// rows = items.reduce((acc, val) => {
		// 	const historyItems = expandedHistoryItems[`${val.id}`];
		// 	const items = expandedItems[`${val.id}`];
		//
		// 	if (historyItems && historyItems.length && Number(val.row_number) === 1) {
		// 		return [ ...acc, val, ...historyItems];
		// 	}
		// 	if (items && items.length && Number(val.row_number) === 1) {
		// 		return [ ...acc, val, ...items];
		// 	}
		// 	return [...acc, val];
		// }, []);

		return {
			headers: values(table.columns),
			rows: rows,
			sortedColumn: table.sortByColumn
		};
	},
	getFilters: state => filters.selectors.getFilters(
		state => state.guarantApproveRequestsList.filters,
		state => state
	)(state),
	getManualApproveInitialValues: state => state.guarantApproveRequestsList.manualApproveInitialValues,
	getIntl: state => intl(state),
	getProgressBar: state => state.guarantApproveRequestsList.showProgressBar,
	getChangeDatePopupRequestId: state => state.guarantApproveRequestsList.changeDatePopupRequestId,
	getChangeDatePopupRequestSid: state => state.guarantApproveRequestsList.changeDatePopupRequestSid,
	getExpandedIds: state => state.guarantApproveRequestsList.expandedIds,
	getExpandedHistoryIds: state => state.guarantApproveRequestsList.expandedHistoryIds,
	getPagination: state => state.guarantApproveRequestsList.table.pagination,
	getExpandedHistoryItems: state => state.guarantApproveRequestsList.expandedHistoryItems,
	getIsProlongPopupVisible: state => state.guarantApproveRequestsList.isProlongPopupVisible,
	getIsChangeDatePopupVisible: state => state.guarantApproveRequestsList.isChangeDatePopupVisible,
	getProlongPopupRequestId: state => state.guarantApproveRequestsList.prolongPopupRequestId,
	getProlongRequestTypes: state => createSelectOptionsFromSysEnums(
		state.root.lists['pending-prolong-request-types']
	),
	getProlongRequestReasons: state => createSelectOptionsFromSysEnums(
		state.root.lists['prolong-request-reasons']
	),
	getCan: state => checkPermissions(state, allowedPermissions),
	getError: state => state.guarantApproveRequestsList.error
};

const request = (dispatch, getState, isSubsequentRequest = false) => {
	const query = {};

	const calendar = selectors.getCalendarOfCreationDate(getState()).value;
	const sort = selectors.getSort(getState());
	const expandedIds = selectors.getExpandedIds(getState());
	const selectedFilters = selectors.getFilters(getState()).selectedFilters;
	query.pagination = getState().guarantApproveRequestsList.table.pagination;

	for (const filter of selectedFilters) {
		if (!filter.value || !filter.value[0] || !filter.name) {
			continue;
		}
		query.filters = query.filters || {};
		query.filters[filter.name]  = filter.value.map(val => val.value);
	}
	query.expandedIds = expandedIds;
	query.date_range = { start: `${calendar.start}T00:00:00Z`, end: `${calendar.end}T23:59:59Z` };

	if (sort && sort.col) {
		query.sort = sort;
	}

	return http({
		url: `${endpoints.MANUAL_GUARANT_REQUEST.BASE}?${qs.stringify(query)}`,
		method: 'get',
		actions: [ !isSubsequentRequest ? FETCH_DATA_SUCCESS : APPEND_DATA_SUCCESS, FETCH_DATA_ERROR]
	})(dispatch)
		.then((action) => {
			if (!_get(action, 'payload.data.rows.length')) {
				dispatch({ type: LOADED_ALL });
			}
		});
};



// WEBPACK FOOTER //
// ./frontend/admin-dashboard/src/modules/guarant-approve-register/list/index.js